import { getClientID, localStorage } from "../utils";

export class NotAuthorizedError extends Error {}

export default class ApiModule {
  baseURL: string;
  path: string;

  constructor(baseURL: string, path: string) {
    this.baseURL = baseURL;
    this.path = path;
  }

  get url(): string {
    return `${this.baseURL}/${this.path}`;
  }

  get clientID(): string {
    return getClientID();
  }

  get jwtToken(): string | null {
    return localStorage.getItem("jwt-token");
  }

  async _resolve<ReturnType>(request: Request): Promise<ReturnType> {
    let response, result;
    try {
      response = await fetch(request);
      const contentType = response.headers
        .get("Content-Type")
        ?.split(";")
        ?.at(0);
      switch (contentType) {
        case "application/json": {
          result = await response.json();
          break;
        }
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
          result = await response.blob();
          break;
        }
        default: {
          // If we don't specifically handle the type, we probably have undefined
          result = await response.text();
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        const reason: Record<string, string[]> = {};
        reason[error.constructor.name] = [error.message];
        throw reason;
      }
      throw error;
    }

    if (response.ok) {
      return result;
    } else if (response.status === 401) {
      throw new NotAuthorizedError(result.error);
    } else {
      throw result;
    }
  }

  /**
   * Convert FormData to params
   * @param {FormData} formData
   *
   * @returns {URLSearchParams}
   */
  _convertParams(formData: FormData) {
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      params.append(key, value as string);
    }
    return params;
  }

  /**
   * @protected
   */
  _defaultHeaders() {
    return {
      Accept: "application/json",
      Authorization: this.jwtToken || "",
      "client-id": this.clientID || "",
      "Content-Type": "application/json",
    };
  }
}
